import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Brain, TrendingUp, Rocket, 
  Target, Award, Globe, Users, Cpu, CheckCircle, AlertTriangle,
  DollarSign, Lightbulb, Sparkles, Atom
} from 'lucide-react';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

interface TrendData {
  id: string;
  title: string;
  category: 'AI' | 'Cloud' | 'Security' | 'DevOps' | 'Data' | 'IoT';
  impact: 'High' | 'Medium' | 'Low';
  maturity: 'Emerging' | 'Growing' | 'Mature';
  adoption: number;
  roi: number;
  description: string;
  benefits: string[];
  challenges: string[];
  timeline: string;
  vendors: string[];
  aiInsight: string;
  recommendation: string;
}

interface AITrend {
  id: string;
  name: string;
  description: string;
  hypeLevel: number;
  practicalValue: number;
  adoptionRate: number;
  marketSize: number;
  keyPlayers: string[];
  useCases: string[];
  futureOutlook: string;
}

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  icon: any;
  color: string;
}

interface VeilleTechnologiqueAIProps {
  isOpen: boolean;
  onClose: () => void;
}

const aiTrends: AITrend[] = [
  {
    id: 'gpt-5',
    name: 'GPT-5 & Multimodal AI',
    description: 'Intelligence artificielle générative avancée avec capacités multimodales',
    hypeLevel: 95,
    practicalValue: 88,
    adoptionRate: 65,
    marketSize: 15.2,
    keyPlayers: ['OpenAI', 'Google', 'Microsoft', 'Anthropic'],
    useCases: ['Code Generation', 'Content Creation', 'Customer Service', 'Data Analysis'],
    futureOutlook: 'Révolutionnaire - Transformera tous les secteurs d\'ici 2025'
  },
  {
    id: 'ai-agents',
    name: 'AI Agents Autonomes',
    description: 'Agents IA capables d\'exécuter des tâches complexes de manière autonome',
    hypeLevel: 85,
    practicalValue: 75,
    adoptionRate: 35,
    marketSize: 8.7,
    keyPlayers: ['LangChain', 'AutoGPT', 'Microsoft Copilot', 'Google Bard'],
    useCases: ['Process Automation', 'Decision Making', 'Task Orchestration', 'Workflow Management'],
    futureOutlook: 'Croissance explosive - Adoption massive prévue 2024-2026'
  },
  {
    id: 'edge-ai',
    name: 'Edge AI & IoT',
    description: 'Intelligence artificielle déployée en périphérie pour traitement temps réel',
    hypeLevel: 70,
    practicalValue: 92,
    adoptionRate: 45,
    marketSize: 12.3,
    keyPlayers: ['NVIDIA', 'Intel', 'Qualcomm', 'ARM'],
    useCases: ['Smart Manufacturing', 'Autonomous Vehicles', 'Smart Cities', 'Healthcare'],
    futureOutlook: 'Essentiel - Base de l\'IoT intelligent et des systèmes critiques'
  },
  {
    id: 'quantum-ai',
    name: 'Quantum AI',
    description: 'Intelligence artificielle quantique pour résoudre des problèmes complexes',
    hypeLevel: 60,
    practicalValue: 40,
    adoptionRate: 5,
    marketSize: 2.1,
    keyPlayers: ['IBM', 'Google', 'Microsoft', 'Rigetti'],
    useCases: ['Optimization', 'Cryptography', 'Drug Discovery', 'Financial Modeling'],
    futureOutlook: 'Futuriste - Potentiel énorme mais adoption lente (5-10 ans)'
  }
];

const techTrends: TrendData[] = [
  {
    id: 'ai-ops',
    title: 'AIOps - Intelligence Artificielle pour les Opérations',
    category: 'AI',
    impact: 'High',
    maturity: 'Growing',
    adoption: 78,
    roi: 340,
    description: 'Automatisation intelligente des opérations TI avec IA prédictive',
    benefits: ['Réduction 60% des incidents', 'Prédiction proactive', 'Optimisation automatique'],
    challenges: ['Complexité d\'implémentation', 'Formation équipes', 'Intégration legacy'],
    timeline: '6-12 mois',
    vendors: ['Splunk', 'Datadog', 'New Relic', 'Dynatrace'],
    aiInsight: 'L\'IA prédit 85% des pannes avant qu\'elles n\'affectent les utilisateurs',
    recommendation: 'Priorité absolue - ROI immédiat et impact transformationnel'
  },
  {
    id: 'cloud-native-ai',
    title: 'Cloud-Native AI Platforms',
    category: 'Cloud',
    impact: 'High',
    maturity: 'Mature',
    adoption: 82,
    roi: 280,
    description: 'Plateformes cloud optimisées pour le déploiement et la gestion d\'IA',
    benefits: ['Scalabilité automatique', 'Coûts optimisés', 'Time-to-market réduit'],
    challenges: ['Vendor lock-in', 'Sécurité des données', 'Formation équipes'],
    timeline: '3-6 mois',
    vendors: ['AWS', 'Azure', 'GCP', 'IBM Cloud'],
    aiInsight: 'Les entreprises cloud-native déploient 3x plus rapidement leurs modèles IA',
    recommendation: 'Adoption recommandée - Standard de l\'industrie'
  },
  {
    id: 'ai-security',
    title: 'AI-Powered Cybersecurity',
    category: 'Security',
    impact: 'High',
    maturity: 'Growing',
    adoption: 71,
    roi: 420,
    description: 'Cybersécurité renforcée par l\'intelligence artificielle',
    benefits: ['Détection avancée', 'Réponse automatique', 'Réduction faux positifs'],
    challenges: ['Complexité IA', 'Évolution menaces', 'Formation spécialisée'],
    timeline: '4-8 mois',
    vendors: ['CrowdStrike', 'SentinelOne', 'Darktrace', 'Palo Alto'],
    aiInsight: 'L\'IA détecte 95% des menaces avancées que les systèmes traditionnels manquent',
    recommendation: 'Critique - Protection essentielle contre les menaces modernes'
  }
];

const createSlides = (): Slide[] => [
  {
    id: 'ai-overview',
    title: 'Révolution IA 2024 - Vue d\'Ensemble',
    subtitle: 'Intelligence Artificielle : L\'avenir est maintenant',
    icon: Brain,
    color: '#8E44AD',
    content: (
      <div className="space-y-6">
        {/* Header Impact IA */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-xl">
          <div className="flex items-center gap-4 mb-4">
            <Brain className="w-12 h-12" />
            <div>
              <h3 className="text-3xl font-bold">Révolution IA 2024</h3>
              <p className="text-white/90">L'intelligence artificielle transforme chaque secteur</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">$2.8T</div>
              <div className="text-white/80 text-sm">Marché IA 2030</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">+47%</div>
              <div className="text-white/80 text-sm">Croissance annuelle</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">85%</div>
              <div className="text-white/80 text-sm">Entreprises adoptent IA</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">340%</div>
              <div className="text-white/80 text-sm">ROI moyen IA</div>
            </div>
          </div>
        </div>

        {/* Tendances IA Principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {aiTrends.slice(0, 4).map((trend) => (
            <div key={trend.id} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">{trend.name}</h4>
                  <p className="text-sm text-gray-600">{trend.description}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Niveau de Hype</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-2 bg-gradient-to-r from-yellow-400 to-red-500 rounded-full"
                        style={{ width: `${trend.hypeLevel}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{trend.hypeLevel}%</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Valeur Pratique</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-2 bg-gradient-to-r from-green-400 to-blue-500 rounded-full"
                        style={{ width: `${trend.practicalValue}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{trend.practicalValue}%</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Taux d'Adoption</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full"
                        style={{ width: `${trend.adoptionRate}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{trend.adoptionRate}%</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                <p className="text-sm text-gray-700 font-medium">{trend.futureOutlook}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: 'ai-radar',
    title: 'Radar IA - Analyse Comparative',
    subtitle: 'Évaluation des technologies IA par impact et maturité',
    icon: Target,
    color: '#E74C3C',
    content: (
      <div className="space-y-6">
        {/* Spider Chart IA */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center flex items-center justify-center gap-2">
            <Target className="w-6 h-6 text-red-600" />
            Radar des Technologies IA
          </h3>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={[
                { technology: 'GPT-5', impact: 95, maturity: 85, adoption: 65, roi: 88 },
                { technology: 'AI Agents', impact: 85, maturity: 70, adoption: 35, roi: 75 },
                { technology: 'Edge AI', impact: 80, maturity: 90, adoption: 45, roi: 92 },
                { technology: 'Quantum AI', impact: 60, maturity: 30, adoption: 5, roi: 40 },
                { technology: 'AIOps', impact: 90, maturity: 80, adoption: 78, roi: 95 },
                { technology: 'AI Security', impact: 88, maturity: 75, adoption: 71, roi: 90 }
              ]}>
                <PolarGrid />
                <PolarAngleAxis dataKey="technology" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar
                  name="Impact"
                  dataKey="impact"
                  stroke="#E74C3C"
                  fill="#E74C3C"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
                <Radar
                  name="Maturité"
                  dataKey="maturity"
                  stroke="#27AE60"
                  fill="#27AE60"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
                <Radar
                  name="Adoption"
                  dataKey="adoption"
                  stroke="#3498DB"
                  fill="#3498DB"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
                <Radar
                  name="ROI"
                  dataKey="roi"
                  stroke="#F39C12"
                  fill="#F39C12"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>Impact</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Maturité</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span>Adoption</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-orange-500 rounded"></div>
              <span>ROI</span>
            </div>
          </div>
        </div>

        {/* Analyse par Catégorie */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Technologies Émergentes
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800">GPT-5 & Multimodal</div>
                  <div className="text-sm text-gray-600">Révolution générative</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-orange-600">95%</div>
                  <div className="text-xs text-gray-500">Impact</div>
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800">AI Agents Autonomes</div>
                  <div className="text-sm text-gray-600">Automatisation avancée</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-purple-600">85%</div>
                  <div className="text-xs text-gray-500">Impact</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              Technologies Matures
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800">Edge AI & IoT</div>
                  <div className="text-sm text-gray-600">Traitement temps réel</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-600">92%</div>
                  <div className="text-xs text-gray-500">ROI</div>
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800">AIOps</div>
                  <div className="text-sm text-gray-600">Opérations intelligentes</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">95%</div>
                  <div className="text-xs text-gray-500">ROI</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'ai-recommendations',
    title: 'Recommandations IA Stratégiques',
    subtitle: 'Roadmap d\'adoption IA pour maximiser le ROI',
    icon: Rocket,
    color: '#27AE60',
    content: (
      <div className="space-y-6">
        {/* Roadmap IA */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Rocket className="w-8 h-8" />
            Roadmap IA 2024-2025
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-bold mb-2">Q1 2024 - Fondations</h4>
              <ul className="text-sm space-y-1">
                <li>• Audit infrastructure IA</li>
                <li>• Formation équipes</li>
                <li>• POC AIOps</li>
                <li>• Stratégie données</li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-bold mb-2">Q2-Q3 2024 - Déploiement</h4>
              <ul className="text-sm space-y-1">
                <li>• IA générative</li>
                <li>• Automatisation processus</li>
                <li>• Sécurité IA</li>
                <li>• Analytics avancés</li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-bold mb-2">Q4 2024-2025 - Innovation</h4>
              <ul className="text-sm space-y-1">
                <li>• AI Agents</li>
                <li>• Edge AI</li>
                <li>• IA prédictive</li>
                <li>• Transformation digitale</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Recommandations Détaillées */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-yellow-600" />
            Recommandations Prioritaires
          </h3>
          <div className="space-y-4">
            {techTrends.map((trend) => (
              <div key={trend.id} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">{trend.title}</h4>
                    <p className="text-gray-600 mb-3">{trend.description}</p>
                    
                    <div className="flex items-center gap-4 mb-3">
                      <span className={`px-3 py-1 text-xs rounded-full ${
                        trend.impact === 'High' ? 'bg-red-100 text-red-800' :
                        trend.impact === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        Impact: {trend.impact}
                      </span>
                      <span className={`px-3 py-1 text-xs rounded-full ${
                        trend.maturity === 'Mature' ? 'bg-green-100 text-green-800' :
                        trend.maturity === 'Growing' ? 'bg-blue-100 text-blue-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        Maturité: {trend.maturity}
                      </span>
                      <span className="text-sm text-gray-600">ROI: {trend.roi}%</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{trend.roi}%</div>
                    <div className="text-sm text-gray-500">ROI</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Bénéfices
                    </h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {trend.benefits.map((benefit, index) => (
                        <li key={index}>• {benefit}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-600" />
                      Défis
                    </h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {trend.challenges.map((challenge, index) => (
                        <li key={index}>• {challenge}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Brain className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div>
                      <h6 className="font-semibold text-gray-800 mb-1">Insight IA</h6>
                      <p className="text-sm text-gray-700 mb-2">{trend.aiInsight}</p>
                      <p className="text-sm font-medium text-purple-700">{trend.recommendation}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'ai-future',
    title: 'Futur de l\'IA - Vision 2030',
    subtitle: 'Prédictions et opportunités pour les 5 prochaines années',
    icon: Atom,
    color: '#3498DB',
    content: (
      <div className="space-y-6">
        {/* Vision 2030 */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Atom className="w-8 h-8" />
            Vision IA 2030
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold mb-3">Transformations Majeures</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  IA Générale Artificielle (AGI)
                </li>
                <li className="flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  Interface Cerveau-Ordinateur
                </li>
                <li className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  IA Quantique Commerciale
                </li>
                <li className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  IA Collaborative Humain-Machine
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Impact Économique</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  $15T+ PIB mondial impacté
                </li>
                <li className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  +40% productivité globale
                </li>
                <li className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  50M+ emplois créés
                </li>
                <li className="flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Innovation exponentielle
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Prédictions par Secteur */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-blue-600" />
              Technologies Clés 2025-2030
            </h4>
            <div className="space-y-3">
              <div className="p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
                <div className="font-semibold text-gray-800">AGI (Intelligence Générale)</div>
                <div className="text-sm text-gray-600">2027-2030 • Révolution complète</div>
                <div className="text-xs text-purple-600 mt-1">Impact: Transformateur</div>
              </div>
              <div className="p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                <div className="font-semibold text-gray-800">IA Quantique</div>
                <div className="text-sm text-gray-600">2026-2028 • Calculs impossibles</div>
                <div className="text-xs text-green-600 mt-1">Impact: Révolutionnaire</div>
              </div>
              <div className="p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg">
                <div className="font-semibold text-gray-800">IA Emotive</div>
                <div className="text-sm text-gray-600">2025-2027 • Compréhension émotions</div>
                <div className="text-xs text-orange-600 mt-1">Impact: Disruptif</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Opportunités d'Investissement
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                <div>
                  <div className="font-semibold text-gray-800">Infrastructure IA</div>
                  <div className="text-sm text-gray-600">GPU, TPU, Edge Computing</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">$500B</div>
                  <div className="text-xs text-gray-500">Marché 2030</div>
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                <div>
                  <div className="font-semibold text-gray-800">IA Appliquée</div>
                  <div className="text-sm text-gray-600">Secteurs verticaux</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-purple-600">$1.2T</div>
                  <div className="text-xs text-gray-500">Marché 2030</div>
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg">
                <div>
                  <div className="font-semibold text-gray-800">IA Éthique</div>
                  <div className="text-sm text-gray-600">Gouvernance, conformité</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-600">$200B</div>
                  <div className="text-xs text-gray-500">Marché 2030</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-6 rounded-xl">
          <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Rocket className="w-6 h-6" />
            Prêt pour la Révolution IA ?
          </h3>
          <p className="mb-4">
            L'IA transforme le monde à une vitesse sans précédent. Les entreprises qui agissent maintenant 
            domineront leurs marchés d'ici 2030. Ne laissez pas cette opportunité vous échapper !
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg text-center">
              <div className="text-2xl font-bold">340%</div>
              <div className="text-sm">ROI Moyen IA</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg text-center">
              <div className="text-2xl font-bold">6 mois</div>
              <div className="text-sm">Time-to-Value</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg text-center">
              <div className="text-2xl font-bold">85%</div>
              <div className="text-sm">Réduction Coûts</div>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

export default function VeilleTechnologiqueAI({ isOpen, onClose }: VeilleTechnologiqueAIProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = createSlides();

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Main Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: currentSlideData.color }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <currentSlideData.icon className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{currentSlideData.title}</h2>
                      {currentSlideData.subtitle && (
                        <p className="text-white/90">{currentSlideData.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Slide {currentSlide + 1} sur {slides.length}</span>
                    <span>{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      className="bg-white h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentSlideData.content}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-bz-blue' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-bz-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
